import React, { useState } from 'react';
import { Eye, Trash2, Search, X, Loader2, Mail, Phone, Calendar, ClipboardList, Star, Car, Shield, User, Edit2, Save, Link2, CheckCircle2, KeyRound, Copy } from 'lucide-react';
import { api } from '../../services/api';

export function UsersTab({ users, onRefresh }) {
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [orphansLoading, setOrphansLoading] = useState(false);
  const [orphansData, setOrphansData] = useState(null);
  const [selectedOrphans, setSelectedOrphans] = useState([]);
  const [reassigning, setReassigning] = useState(false);
  const [showPwdSection, setShowPwdSection] = useState(false);
  const [newPwd, setNewPwd] = useState('');
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdSaved, setPwdSaved] = useState(false);

  const filtered = users.filter(u => {
    const matchesSearch = !search ||
      (u.name || '').toLowerCase().includes(search.toLowerCase()) ||
      (u.email || '').toLowerCase().includes(search.toLowerCase()) ||
      (u.phone || '').toLowerCase().includes(search.toLowerCase());
    const matchesRole = roleFilter === 'all' || u.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const openDetail = async (userId) => {
    setSelectedUser(userId);
    setDetailLoading(true);
    setUserDetail(null);
    setEditing(false);
    try {
      const res = await api.get(`/admin/user/${userId}`);
      setUserDetail(res.data);
    } catch (e) {
      setUserDetail({ error: e.response?.data?.detail || 'Fehler beim Laden' });
    } finally { setDetailLoading(false); }
  };

  const startEdit = () => {
    setEditing(true);
    setEditForm({
      name: userDetail.name || '',
      email: userDetail.email || '',
      phone: userDetail.phone || '',
    });
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/user/${selectedUser}/edit`, editForm);
      const res = await api.get(`/admin/user/${selectedUser}`);
      setUserDetail(res.data);
      setEditing(false);
      onRefresh();
    } catch (e) { alert(e.response?.data?.detail || 'Fehler'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`Benutzer "${userName}" wirklich löschen?`)) return;
    setDeleting(userId);
    try {
      await api.delete(`/admin/user/${userId}`);
      setSelectedUser(null);
      setUserDetail(null);
      onRefresh();
    } catch (e) { alert(e.response?.data?.detail || 'Fehler'); }
    finally { setDeleting(null); }
  };

  const findOrphans = async () => {
    if (!userDetail?.email) return;
    setOrphansLoading(true);
    setOrphansData(null);
    setSelectedOrphans([]);
    try {
      const res = await api.get(`/admin/customer-orphans`, { params: { email: userDetail.email } });
      setOrphansData(res.data);
      // Pre-select all orphans by default
      setSelectedOrphans((res.data.orphans || []).map(o => o.id));
    } catch (e) {
      alert(e.response?.data?.detail || 'Fehler beim Suchen');
    } finally { setOrphansLoading(false); }
  };

  const reassignSelected = async () => {
    if (!selectedOrphans.length || !userDetail?.id) return;
    if (!window.confirm(`${selectedOrphans.length} Buchung(en) ${userDetail.name || userDetail.email} zuweisen?`)) return;
    setReassigning(true);
    try {
      const res = await api.post(`/admin/reassign-transfers`, {
        transfer_ids: selectedOrphans,
        target_user_id: userDetail.id,
      });
      alert(`${res.data.modified} Buchung(en) erfolgreich zugewiesen.`);
      // Refresh orphans view
      await findOrphans();
      // Refresh detail (so transfer count updates)
      const dr = await api.get(`/admin/user/${userDetail.id}`);
      setUserDetail(dr.data);
    } catch (e) {
      alert(e.response?.data?.detail || 'Fehler beim Zuweisen');
    } finally { setReassigning(false); }
  };

  const closeModal = () => {
    setSelectedUser(null);
    setUserDetail(null);
    setEditing(false);
    setOrphansData(null);
    setSelectedOrphans([]);
    setShowPwdSection(false);
    setNewPwd('');
    setPwdSaved(false);
  };

  const generatePwd = () => {
    // Friendly-readable password: 3 letters + 4 digits + 1 letter
    const chars = 'abcdefghijkmnpqrstuvwxyz';
    const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const digits = '23456789';
    const pick = (set, n) => Array.from({ length: n }, () => set[Math.floor(Math.random() * set.length)]).join('');
    return pick(upper, 1) + pick(chars, 3) + pick(digits, 4);
  };

  const savePassword = async () => {
    if (!newPwd || newPwd.length < 6) {
      alert('Passwort muss mindestens 6 Zeichen lang sein.');
      return;
    }
    if (!window.confirm(`Passwort für ${userDetail.email} jetzt setzen?`)) return;
    setPwdSaving(true);
    setPwdSaved(false);
    try {
      await api.put(`/admin/user/${userDetail.id}/password`, { password: newPwd });
      setPwdSaved(true);
    } catch (e) {
      alert(e.response?.data?.detail || 'Fehler beim Setzen des Passworts');
    } finally { setPwdSaving(false); }
  };

  const copyPwd = async () => {
    try {
      await navigator.clipboard.writeText(newPwd);
      alert('Passwort in Zwischenablage kopiert.');
    } catch { /* ignore */ }
  };

  const roleLabel = (r) => ({ admin: 'Admin', driver: 'Fahrer', company: 'Unternehmen' }[r] || 'Kunde');
  const roleClass = (r) => ({ admin: 'bg-purple-100 text-purple-700', driver: 'bg-green-100 text-green-700', company: 'bg-blue-100 text-blue-700' }[r] || 'bg-slate-100 text-slate-600');

  return (
    <div className="space-y-4" data-testid="admin-users-tab">
      <div className="bg-white rounded-xl border border-slate-100 p-4">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Suche nach Name, E-Mail oder Telefon..."
              className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm" data-testid="users-search-input" />
          </div>
          <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white" data-testid="users-role-filter">
            <option value="all">Alle Rollen</option>
            <option value="customer">Kunden</option>
            <option value="driver">Fahrer</option>
            <option value="company">Unternehmen</option>
            <option value="admin">Admin</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100">
          <h2 className="font-bold text-sm text-slate-800">Benutzer ({filtered.length})</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Name</th>
                <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">E-Mail</th>
                <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Rolle</th>
                <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Registriert</th>
                <th className="text-right px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filtered.map(u => (
                <tr key={u.id} className="hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                  <td className="px-5 py-3 text-sm font-medium text-slate-800">{u.name || '-'}</td>
                  <td className="px-5 py-3 text-sm text-slate-500">{u.email}</td>
                  <td className="px-5 py-3"><span className={`px-2 py-0.5 rounded text-xs font-medium ${roleClass(u.role)}`}>{roleLabel(u.role)}</span></td>
                  <td className="px-5 py-3 text-sm text-slate-500">{u.created_at ? new Date(u.created_at).toLocaleDateString('de-DE') : '-'}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <button onClick={() => openDetail(u.id)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-testid={`view-user-${u.id}`}><Eye size={16} /></button>
                      {u.role !== 'admin' && (
                        <button onClick={() => handleDelete(u.id, u.name || u.email)} disabled={deleting === u.id} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50" data-testid={`delete-user-${u.id}`}>
                          {deleting === u.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-sm text-slate-400 text-center">Keine Benutzer gefunden</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* User Detail/Edit Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={closeModal} data-testid="user-detail-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white px-6 py-4 border-b border-slate-100 flex items-center justify-between z-10">
              <h2 className="font-bold text-slate-800">{editing ? 'Benutzer bearbeiten' : 'Benutzer-Details'}</h2>
              <button onClick={closeModal} className="p-1.5 hover:bg-slate-100 rounded-lg" data-testid="close-user-detail"><X size={18} className="text-slate-400" /></button>
            </div>

            {detailLoading ? (
              <div className="p-10 flex justify-center"><Loader2 size={24} className="animate-spin text-slate-300" /></div>
            ) : userDetail?.error ? (
              <div className="p-6 text-sm text-red-600">{userDetail.error}</div>
            ) : userDetail ? (
              <div className="p-6 space-y-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center"><User size={24} className="text-slate-400" /></div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-800">{userDetail.name || '-'}</h3>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${roleClass(userDetail.role)}`}>{roleLabel(userDetail.role)}</span>
                    </div>
                  </div>
                  {!editing && (
                    <button onClick={startEdit} className="flex items-center gap-1 px-3 py-1.5 bg-orange-50 text-orange-600 rounded-lg text-xs font-medium hover:bg-orange-100" data-testid="edit-user-btn">
                      <Edit2 size={12} /> Bearbeiten
                    </button>
                  )}
                </div>

                {editing ? (
                  <div className="space-y-3">
                    {[
                      { key: 'name', label: 'Name', icon: User },
                      { key: 'email', label: 'E-Mail', icon: Mail, type: 'email' },
                      { key: 'phone', label: 'Telefon', icon: Phone, type: 'tel' },
                    ].map(f => (
                      <div key={f.key}>
                        <label className="text-xs font-medium text-slate-500 mb-1 flex items-center gap-1"><f.icon size={12} /> {f.label}</label>
                        <input type={f.type || 'text'} value={editForm[f.key] || ''} onChange={e => setEditForm(p => ({ ...p, [f.key]: e.target.value }))}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid={`edit-user-${f.key}`} />
                      </div>
                    ))}
                    <div className="flex gap-2 pt-2">
                      <button onClick={saveEdit} disabled={saving} className="flex items-center gap-1 px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 disabled:opacity-50" data-testid="save-user-btn">
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Speichern
                      </button>
                      <button onClick={() => setEditing(false)} className="px-4 py-2 bg-slate-100 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-200">Abbrechen</button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-3">
                    <InfoRow icon={Mail} label="E-Mail" value={userDetail.email} />
                    <InfoRow icon={Phone} label="Telefon" value={userDetail.phone || '-'} />
                    <InfoRow icon={Calendar} label="Registriert" value={userDetail.created_at ? new Date(userDetail.created_at).toLocaleString('de-DE') : '-'} />
                    <InfoRow icon={Shield} label="Verifiziert" value={userDetail.is_verified ? 'Ja' : 'Nein'} />
                    <InfoRow icon={ClipboardList} label="Transfers" value={userDetail.total_transfers || 0} />
                    <InfoRow icon={Star} label="Bewertungen" value={userDetail.ratings_given || 0} />
                  </div>
                )}

                {userDetail.driver_profile && (
                  <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                    <p className="text-xs font-semibold text-green-700 mb-1 flex items-center gap-1"><Car size={12} /> Fahrer-Profil</p>
                    <div className="flex items-center gap-4 text-xs text-green-800">
                      <span>Status: <b>{userDetail.driver_profile.status}</b></span>
                      <span>Bewertung: <b>{userDetail.driver_profile.rating || '-'}</b></span>
                      <span>Fahrten: <b>{userDetail.driver_profile.total_trips || 0}</b></span>
                    </div>
                  </div>
                )}

                {userDetail.role !== 'admin' && !editing && (
                  <button onClick={() => handleDelete(userDetail.id, userDetail.name || userDetail.email)} disabled={deleting === userDetail.id}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-sm font-medium disabled:opacity-50" data-testid="user-detail-delete-btn">
                    {deleting === userDetail.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Benutzer löschen
                  </button>
                )}

                {/* Set password (for Google-login users who need to use the mobile app) */}
                {!editing && (
                  <div className="border-t border-slate-100 pt-4">
                    {!showPwdSection ? (
                      <button
                        onClick={() => { setShowPwdSection(true); setNewPwd(generatePwd()); setPwdSaved(false); }}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg text-sm font-medium"
                        data-testid="open-set-password-btn"
                      >
                        <KeyRound size={14} /> Passwort setzen (für Mobile App)
                      </button>
                    ) : (
                      <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 space-y-3">
                        <div className="flex items-start gap-2">
                          <KeyRound size={18} className="text-indigo-600 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-indigo-900">Passwort für {userDetail.name || userDetail.email}</p>
                            <p className="text-xs text-indigo-700 mt-0.5">Setze ein Passwort, damit der User sich in der Mobile App anmelden kann (z.B. nach Google-Login-Anmeldung ohne Passwort).</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={newPwd}
                            onChange={(e) => { setNewPwd(e.target.value); setPwdSaved(false); }}
                            className="flex-1 px-3 py-2 border border-indigo-200 rounded-lg text-sm font-mono bg-white focus:border-indigo-500 outline-none"
                            placeholder="Passwort eingeben"
                            data-testid="set-password-input"
                          />
                          <button
                            onClick={() => { setNewPwd(generatePwd()); setPwdSaved(false); }}
                            className="px-3 py-2 bg-white border border-indigo-200 hover:bg-indigo-100 text-indigo-700 rounded-lg text-xs font-medium"
                            title="Neues Passwort generieren"
                            data-testid="regenerate-password-btn"
                          >
                            ↻
                          </button>
                          <button
                            onClick={copyPwd}
                            className="px-3 py-2 bg-white border border-indigo-200 hover:bg-indigo-100 text-indigo-700 rounded-lg text-xs font-medium"
                            title="In Zwischenablage kopieren"
                            data-testid="copy-password-btn"
                          >
                            <Copy size={14} />
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={savePassword}
                            disabled={pwdSaving || !newPwd}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-semibold disabled:opacity-50"
                            data-testid="save-password-btn"
                          >
                            {pwdSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Passwort speichern
                          </button>
                          <button
                            onClick={() => { setShowPwdSection(false); setNewPwd(''); setPwdSaved(false); }}
                            className="px-4 py-2 bg-white border border-indigo-200 hover:bg-indigo-100 text-indigo-700 rounded-lg text-sm"
                            data-testid="cancel-password-btn"
                          >
                            Abbrechen
                          </button>
                        </div>
                        {pwdSaved && (
                          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-800" data-testid="password-saved-msg">
                            ✓ Passwort wurde gespeichert. Bitte das Passwort jetzt sicher an <b>{userDetail.email}</b> übermitteln (z.B. per WhatsApp/SMS).
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Orphan transfers: find & reassign by email */}
                {!editing && userDetail.role !== 'admin' && (
                  <div className="border-t border-slate-100 pt-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                          <Link2 size={14} className="text-orange-500" /> Verwaiste Buchungen & Diagnose
                        </h3>
                        <p className="text-xs text-slate-500 mt-0.5">Prüft Duplikat-User und Transfer-Verknüpfungen</p>
                      </div>
                      <button onClick={findOrphans} disabled={orphansLoading}
                        className="px-3 py-1.5 bg-slate-800 text-white rounded-lg text-xs font-medium hover:bg-slate-900 disabled:opacity-50 flex items-center gap-1"
                        data-testid="find-orphans-btn">
                        {orphansLoading ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
                        Diagnose starten
                      </button>
                    </div>

                    {orphansData && (
                      <div className="bg-slate-50 rounded-lg p-3 space-y-3">
                        {/* Duplicate user warning */}
                        {orphansData.duplicate_user_count > 1 && (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <p className="text-xs font-bold text-red-700 mb-2">
                              ⚠️ {orphansData.duplicate_user_count} Benutzer mit dieser E-Mail gefunden!
                            </p>
                            <p className="text-xs text-red-600 mb-2">
                              Das ist die wahrscheinliche Ursache. Beim Login wird einer ausgewählt – die Transfers gehören aber zum anderen.
                            </p>
                            <div className="space-y-1.5">
                              {orphansData.all_users_with_email.map((u, idx) => (
                                <div key={u.id} className="bg-white rounded p-2 text-xs border border-red-200">
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="flex-1">
                                      <div className="font-medium text-slate-800">{u.name || '(kein Name)'} {idx === 0 && <span className="text-orange-600">(Login-User)</span>}</div>
                                      <div className="text-slate-500 break-all">id: <code className="text-xs">{u.id}</code></div>
                                      <div className="text-slate-500">registriert: {u.created_at ? new Date(u.created_at).toLocaleString('de-DE') : '-'}</div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="text-xs text-slate-600 flex flex-wrap gap-3">
                          <span>Per E-Mail: <b>{orphansData.transfers_by_email_count}</b></span>
                          <span>Per User-ID (Login-User): <b>{orphansData.transfers_by_user_id_count}</b></span>
                          <span className="text-orange-600">Verwaist: <b>{orphansData.orphans_count}</b></span>
                        </div>

                        {/* Group breakdown */}
                        {orphansData.transfers_grouped_by_customer_id && Object.keys(orphansData.transfers_grouped_by_customer_id).length > 0 && (
                          <div className="bg-white rounded border border-slate-200 p-2">
                            <p className="text-xs font-semibold text-slate-700 mb-1">Verteilung der Transfers nach customer_id:</p>
                            {Object.entries(orphansData.transfers_grouped_by_customer_id).map(([cid, count]) => (
                              <div key={cid} className="text-xs flex justify-between py-0.5 border-b border-slate-50 last:border-0">
                                <code className="text-slate-600 truncate flex-1 mr-2">{cid}</code>
                                <span className={cid === userDetail.id ? 'text-green-600 font-semibold' : 'text-orange-600 font-semibold'}>
                                  {count} {cid === userDetail.id ? '✓ aktueller User' : '⚠ anderer/leerer User'}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}

                        {orphansData.orphans_count === 0 && orphansData.transfers_by_email_count === 0 && (
                          <p className="text-xs text-slate-500 italic">Keine Buchungen mit dieser E-Mail gefunden.</p>
                        )}

                        {(orphansData.orphans || []).length > 0 && (
                          <>
                            <div className="space-y-1.5 max-h-48 overflow-y-auto">
                              {orphansData.orphans.map(o => (
                                <label key={o.id} className="flex items-start gap-2 p-2 bg-white rounded border border-slate-200 cursor-pointer hover:bg-orange-50" data-testid={`orphan-${o.id}`}>
                                  <input
                                    type="checkbox"
                                    checked={selectedOrphans.includes(o.id)}
                                    onChange={(e) => {
                                      setSelectedOrphans(p => e.target.checked ? [...p, o.id] : p.filter(x => x !== o.id));
                                    }}
                                    className="mt-0.5"
                                  />
                                  <div className="flex-1 text-xs">
                                    <div className="font-medium text-slate-800 truncate">
                                      {o.pickup_location?.address || o.pickup_location || '-'} → {o.dropoff_location?.address || o.dropoff_location || '-'}
                                    </div>
                                    <div className="text-slate-500 flex flex-wrap gap-2 mt-0.5">
                                      <span>{o.pickup_date || '-'}</span>
                                      <span>•</span>
                                      <span>Status: {o.status || '-'}</span>
                                      <span>•</span>
                                      <span className="text-red-600">cid: {(o.customer_id || '-').slice(0,8)}…</span>
                                    </div>
                                  </div>
                                </label>
                              ))}
                            </div>
                            <button
                              onClick={reassignSelected}
                              disabled={reassigning || selectedOrphans.length === 0}
                              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                              data-testid="reassign-orphans-btn"
                            >
                              {reassigning ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                              {selectedOrphans.length} Buchung(en) diesem Kunden (Login-User) zuweisen
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 py-1.5 border-b border-slate-50 last:border-0">
      <Icon size={14} className="text-slate-400 shrink-0" />
      <span className="text-xs text-slate-500 w-36 shrink-0">{label}</span>
      <span className="text-sm text-slate-800 font-medium">{value}</span>
    </div>
  );
}
